import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ShieldCheck, Stethoscope, AlertTriangle, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatExpiry = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Section = ({ label, items, emptyText, testid }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4" data-testid={testid}>
    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
      {label}
    </p>
    {items && items.length > 0 ? (
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li
            key={`${label}-${i}`}
            className="text-sm text-gray-900 leading-snug"
          >
            • {it}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-gray-400 italic">{emptyText}</p>
    )}
  </div>
);

/**
 * Public, read-only health summary page rendered to clinicians.
 *
 * - No auth required (token in URL is the credential)
 * - Renders ONLY allergies, medical_conditions, medications
 * - Optionally emergency_contact (if owner opted in)
 * - Never renders email, full name, role, DOB, gender, height, weight,
 *   reminders, dashboard data, settings — these are not in the response
 * - 404s on revoked / expired / unknown tokens
 */
const ClinicianShareView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/clinician-share/by-token/${encodeURIComponent(token)}`,
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (cancelled) return;
        setError(
          e.response?.data?.detail ||
            'This link is invalid or has expired.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <Loader2 className="h-7 w-7 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <div className="flex justify-center">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-rose-100 text-rose-700">
                <AlertTriangle className="h-6 w-6" />
              </span>
            </div>
            <h1 className="text-lg font-bold text-gray-900">Link not available</h1>
            <p className="text-sm text-gray-600">{error}</p>
            <p className="text-xs text-gray-500 italic">
              Please ask the patient to generate a new share link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="clinician-share-page min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-8 px-4"
      data-testid="clinician-share-view"
    >
      {/* Print-friendly stylesheet — strips chrome, keeps the summary itself
          clean on paper. Only applies when the user (or clinician) prints. */}
      <style>{`
        @media print {
          @page { margin: 14mm; }
          .clinician-share-page {
            background: white !important;
            padding: 0 !important;
          }
          .no-print { display: none !important; }
          .clinician-share-page * {
            box-shadow: none !important;
          }
          .clinician-share-page .border {
            border-color: #d1d5db !important;
          }
        }
      `}</style>

      <div className="max-w-2xl mx-auto space-y-5">
        {/* Print button — visible on screen only */}
        <div className="no-print flex justify-end">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-95 transition-transform shadow-sm"
            data-testid="clinician-share-print-btn"
            aria-label="Print this summary"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>

        {/* Header */}
        <Card className="border border-emerald-100 shadow-sm rounded-2xl bg-white">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700">
                <Stethoscope className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest">
                  HealthLoop · Shared health summary
                </p>
                <h1
                  className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight"
                  data-testid="clinician-share-name"
                >
                  Health summary for {data.first_name}
                </h1>
                <p className="mt-1 text-xs text-gray-500">
                  Read-only · {formatExpiry(data.expires_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy note */}
        <div className="rounded-xl border border-gray-100 bg-white p-3 flex items-start gap-2 text-xs text-gray-600">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <span>
            This page only contains the health information the patient
            chose to share. Account data, contact details, reminders, and
            dashboard analytics are not included.
          </span>
        </div>

        {/* Health information sections */}
        <Section
          label="Allergies"
          items={data.allergies}
          emptyText="None reported"
          testid="clinician-share-allergies"
        />
        <Section
          label="Medical conditions"
          items={data.medical_conditions}
          emptyText="None reported"
          testid="clinician-share-conditions"
        />
        <Section
          label="Medications"
          items={data.medications}
          emptyText="None reported"
          testid="clinician-share-medications"
        />

        {data.emergency_contact && (
          <div className="rounded-xl border border-gray-100 bg-white p-4" data-testid="clinician-share-emergency">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Emergency contact
            </p>
            <p className="text-sm text-gray-900">{data.emergency_contact}</p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pt-4">
          Powered by HealthLoop · Read-only · Link expires automatically
        </p>

        {/* Print-only traceability footer — survives @media print so the
            clinician's chart copy carries provenance, but reveals nothing
            beyond the token suffix the owner already sees in their UI. */}
        <p
          className="hidden print:block text-center text-[10px] text-gray-500 pt-1"
          data-testid="clinician-share-print-footer"
        >
          Generated {data.created_at ? new Date(data.created_at).toLocaleString() : ''}
          {data.share_id_suffix ? ` · Share ID: …${data.share_id_suffix}` : ''}
          {data.expires_at ? ` · Expires ${new Date(data.expires_at).toLocaleString()}` : ''}
        </p>
      </div>
    </div>
  );
};

export default ClinicianShareView;
